import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "../../../ui/form"
import { Input } from "../../../ui/input"
import { Button } from "../../../ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../ui/select"
import { Textarea } from "../../../ui/textarea"
import { useToast } from "../../../hooks/use-toast"
import {
  Briefcase,
  User,
  Mail,
  Phone,
  Linkedin,
  FileText,
  X
} from "lucide-react"

// File size limit in bytes (5MB)
const FILE_SIZE_LIMIT = 5 * 1024 * 1024
const SUPPORTED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
]

// Schema for form validation
const formSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .refine(val => /^[0-9+\- ()]*$/.test(val), {
      message: "Please enter a valid phone number"
    }),
  linkedinUrl: z
    .string()
    .url("Please enter a valid URL")
    .or(z.string().length(0))
    .optional(),
  department: z.string({
    required_error: "Please select a department"
  }),
  position: z.string({
    required_error: "Please select a position"
  }),
  resume: z
    .instanceof(FileList)
    .refine(files => files?.length === 1, "Resume is required")
    .transform(files => files[0])
    .refine(
      file => file && file.size <= FILE_SIZE_LIMIT,
      `File size should be less than 5MB`
    )
    .refine(
      file => SUPPORTED_FILE_TYPES.includes(file.type),
      "Only PDF and DOCX files are supported"
    ),
  additionalInfo: z.string().optional()
})

const JobApplicationForm = ({ onClose, position, departments, positions }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const { toast } = useToast()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      linkedinUrl: "",
      department: "",
      position: position || "",
      additionalInfo: ""
    }
  })

  const onSubmit = async data => {
    setIsSubmitting(true)

    // Simulating API call with a timeout
    setTimeout(() => {
      console.log("Form submitted:", data)
      setIsSubmitting(false)
      setIsSubmitted(true)
      toast({
        title: "Application Submitted",
        description:
          "Thanks for applying! Our team will review your application and get back to you shortly."
      })
    }, 1500)
  }

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-0">
      {!isSubmitted ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold">Job Application</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <User size={16} className="text-gray-500" />
                      Full Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        {...field}
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Mail size={16} className="text-gray-500" />
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="johndoe@example.com"
                        type="email"
                        {...field}
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Phone size={16} className="text-gray-500" />
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+1 (123) 456-7890"
                        {...field}
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="linkedinUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Linkedin size={16} className="text-gray-500" />
                      LinkedIn Profile URL (Optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://linkedin.com/in/johndoe"
                        {...field}
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Briefcase size={16} className="text-gray-500" />
                        Department
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {departments.map(dept => (
                            <SelectItem key={dept} value={dept}>
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="position"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Briefcase size={16} className="text-gray-500" />
                        Position
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select position" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {positions.map(pos => (
                            <SelectItem key={pos} value={pos}>
                              {pos}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="resume"
                render={({ field: { onChange, value, ...fieldProps } }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <FileText size={16} className="text-gray-500" />
                      Resume (PDF or DOCX, max 5MB)
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-center justify-center w-full">
                        <label
                          htmlFor="resume-upload"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all duration-200"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center px-4">
                            <FileText className="w-8 h-8 mb-2 text-gray-500" />
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">
                                Click to upload
                              </span>{" "}
                              or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">
                              PDF or DOCX (max 5MB)
                            </p>
                          </div>
                          <input
                            id="resume-upload"
                            type="file"
                            className="hidden"
                            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={e => {
                              onChange(e.target.files)
                            }}
                            {...fieldProps}
                          />
                        </label>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="additionalInfo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Additional Information (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Share any additional information you would like us to know..."
                        className="resize-none min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col sm:flex-row gap-3 justify-end mt-8">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-oye-purple hover:bg-oye-dark-purple w-full sm:w-auto"
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </form>
          </Form>
        </>
      ) : (
        <div className="text-center py-8 sm:py-10 px-4">
          <div className="w-16 h-16 bg-oye-light-purple rounded-full flex items-center justify-center mx-auto mb-6">
            <FileText className="h-8 w-8 text-oye-purple" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold mb-4">
            Application Submitted!
          </h2>
          <p className="text-gray-600 mb-8">
            Thanks for applying! Our team will review your application and get
            back to you shortly.
          </p>
          <Button
            onClick={onClose}
            className="bg-oye-purple hover:bg-oye-dark-purple"
          >
            Close
          </Button>
        </div>
      )}
    </div>
  )
}

export default JobApplicationForm
